import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { BLACK, RED } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import { formatCurrency, minutesToTimeString, timeStringToMinutes } from '../../helpers';
import { textFieldSx } from '../AutoSaveTextField';

export const SHIFT_GRID_COLUMNS = 'minmax(0, 1fr) 130px 130px 110px 40px';

const monoSx = {
  ...textFieldSx,
  '& .MuiOutlinedInput-input': { fontFamily: 'monospace', fontSize: '13px' },
};

const rowSx = { borderBottom: `1px solid ${BLACK[20]}`, padding: '10px 16px' };

const gridSx = {
  alignItems: 'center',
  columnGap: '12px',
  display: 'grid',
  gridTemplateColumns: SHIFT_GRID_COLUMNS,
};

const nameSx = {
  color: BLACK[100],
  fontFamily: PRIMARY_FONT[500],
  fontSize: '14px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const iconSx = { fontSize: '18px' };

const errorSx = { color: RED[80], fontSize: '12px', marginTop: '4px' };

const toDraft = (shift) => ({
  name: shift.name || '',
  start: minutesToTimeString(shift.start),
  end: minutesToTimeString(shift.end),
});

const ShiftRow = ({ shift, basePayRate, onSave, onRemove, onCancelNew, isNew = false }) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const [draft, setDraft] = useState(() => toDraft(shift));
  const [isEditingName, setIsEditingName] = useState(isNew);
  const [isTouched, setIsTouched] = useState(false);
  const rowRef = useRef(null);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) setDraft(toDraft(shift));
  }, [shift]);

  const start = timeStringToMinutes(draft.start);
  const end = timeStringToMinutes(draft.end);
  let error = null;
  if (!draft.name.trim()) error = 'pt.detail.shifts.nameRequired';
  else if (start === null || end === null) error = 'pt.detail.shifts.timeRequired';

  const commit = () => {
    setIsTouched(true);
    if (error) return;
    setIsEditingName(false);
    const edited = {};
    if (draft.name.trim() !== shift.name) edited.name = draft.name.trim();
    if (start !== shift.start) edited.start = start;
    if (end !== shift.end) edited.end = end;
    if (isNew || Object.keys(edited).length > 0) {
      onSave({ name: draft.name.trim(), start, end, ...edited }, edited);
    }
  };

  const handleRowBlur = (event) => {
    if (rowRef.current?.contains(event.relatedTarget)) return;
    isFocusedRef.current = false;
    commit();
  };

  const handleNameKeyDown = (event) => {
    if (event.key === 'Enter') {
      commit();
    } else if (event.key === 'Escape') {
      if (isNew) {
        onCancelNew();
        return;
      }
      setDraft((prev) => ({ ...prev, name: shift.name || '' }));
      setIsEditingName(false);
    }
  };

  const update = (field) => (event) =>
    setDraft((prev) => ({ ...prev, [field]: event.target.value }));
  const label = draft.name || t('pt.detail.shifts.newShift');

  return (
    <Box
      ref={rowRef}
      data-testid="pt-detail-shift-row"
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={handleRowBlur}
      sx={rowSx}
    >
      <Box sx={gridSx}>
        {isEditingName ? (
          <TextField
            autoFocus
            value={draft.name}
            onChange={update('name')}
            onKeyDown={handleNameKeyDown}
            placeholder={t('pt.detail.shifts.name')}
            size="small"
            inputProps={{ 'aria-label': t('pt.detail.shifts.name') }}
            sx={textFieldSx}
          />
        ) : (
          <Typography sx={nameSx}>{shift.name}</Typography>
        )}
        <TextField
          type="time"
          value={draft.start}
          onChange={update('start')}
          size="small"
          inputProps={{ 'aria-label': t('pt.detail.shifts.start', { shift: label }) }}
          sx={monoSx}
        />
        <TextField
          type="time"
          value={draft.end}
          onChange={update('end')}
          size="small"
          inputProps={{ 'aria-label': t('pt.detail.shifts.end', { shift: label }) }}
          sx={monoSx}
        />
        <TextField
          disabled
          value={formatCurrency(basePayRate)}
          size="small"
          inputProps={{ 'aria-label': t('pt.detail.shifts.payRate', { shift: label }) }}
          sx={monoSx}
        />
        {isEditingName ? (
          <IconButton
            aria-label={t('pt.detail.shifts.remove', { shift: label })}
            onClick={isNew ? onCancelNew : onRemove}
            size="small"
          >
            <DeleteOutlineIcon sx={iconSx} />
          </IconButton>
        ) : (
          <IconButton
            aria-label={t('pt.detail.shifts.edit', { shift: label })}
            onClick={() => setIsEditingName(true)}
            size="small"
          >
            <EditOutlinedIcon sx={iconSx} />
          </IconButton>
        )}
      </Box>
      {error && isTouched && <Typography sx={errorSx}>{t(error)}</Typography>}
    </Box>
  );
};

ShiftRow.propTypes = {
  shift: PropTypes.shape({
    name: PropTypes.string,
    start: PropTypes.number,
    end: PropTypes.number,
  }).isRequired,
  basePayRate: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onCancelNew: PropTypes.func,
  isNew: PropTypes.bool,
};

export default ShiftRow;
